/* eslint-disable @typescript-eslint/no-explicit-any */
import api from '../lib/api';
import { useQuery } from '@tanstack/react-query';

// Dashboard scope types
export type DashboardScope = 'user' | 'team' | 'tenant';

// Dashboard status types
export type DashboardStatus = 'draft' | 'published' | 'archived';

// Dashboard priority types
export type DashboardPriority = 'low' | 'medium' | 'high' | 'critical';

// Main dashboard interface
export interface Dashboard {
  dashboardId: string;
  title: string;
  description: string;
  type: string; // defaults to 'dashboard'
  scope: DashboardScope;
  ownerId: string; // userEmailHash, teamId, etc.
  tenantId: string;
  organizationId: string;
  conversationId?: string;
  status: DashboardStatus;
  dashboardSchema: DashboardSchema;
  meta?: Record<string, any>;
  createdAt: string; // ISO 8601 timestamp
  updatedAt: string; // ISO 8601 timestamp
}

// Dashboard schema containing all dashboard properties
export interface DashboardSchema {
  titleIcon?: string;
  sections: DashboardSection[];
  widgets: DashboardWidget[];
  fileUrl?: string; // S3 URL for generated PDF
  [key: string]: any; // Allow additional properties
}

// Dashboard section
export interface DashboardSection {
  id: string;
  title: string;
  description?: string;
  order: number;
  widgets: string[]; // Widget IDs
  [key: string]: any;
}

// Dashboard widget
export interface DashboardWidget {
  id: string;
  type: string;
  title: string;
  description?: string;
  config: Record<string, any>;
  order: number;
  sectionId: string;
  [key: string]: any;
}

// Flattened dashboard for API responses (dashboardSchema contents at top level)
export interface FlattenedDashboard extends Omit<Dashboard, 'dashboardSchema'> {
  titleIcon?: string;
  sections: DashboardSection[];
  widgets: DashboardWidget[];
  fileUrl?: string;
}

// API Request/Response Types

// List dashboards request
export interface ListDashboardsRequest {
  status?: DashboardStatus;
  limit?: number;
  offset?: number;
  sortBy?: 'createdAt' | 'updatedAt' | 'title';
  sortOrder?: 'asc' | 'desc';
}

// List dashboards response
export interface ListDashboardsResponse {
  dashboards: Dashboard[];
  meta: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

// Get dashboard request
export interface GetDashboardRequest {
  dashboardId: string;
  tenantId: string;
  ownerId: string;
  scope?: DashboardScope;
}

// Get dashboard response (flattened)
export interface GetDashboardResponse {
  dashboard: FlattenedDashboard;
}

// Create/Update dashboard request
export interface CreateDashboardRequest {
  dashboardId?: string; // Optional for upsert functionality
  title: string;
  description: string;
  titleIcon?: string;
  sections: DashboardSection[];
  widgets: DashboardWidget[];
  scope: DashboardScope;
  ownerId: string;
  tenantId: string;
  organizationId: string;
  conversationId?: string;
  status?: DashboardStatus;
  meta?: Record<string, any>;
}

// Create/Update dashboard response
export interface CreateDashboardResponse {
  dashboard: Dashboard;
  activityId?: string;
  fileUrl?: string; // S3 URL for generated PDF
}

// Error Types
export interface ApiError {
  error: string;
  message: string;
  statusCode: number;
  details?: Record<string, any>;
}

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

// Dashboard Service Class
class DashboardService {
  private baseUrl = '/api/core/pleri';

  // List dashboards
  async listDashboards(
    params: ListDashboardsRequest,
  ): Promise<ListDashboardsResponse> {
    const response = await api.post(`${this.baseUrl}/dashboards`, params);
    return response.data;
  }

  // Get dashboard by ID
  async getDashboard(
    dashboardId: string,
    ownerId?: string,
    scope?: DashboardScope,
  ): Promise<GetDashboardResponse> {
    const queryParams = new URLSearchParams();
    if (ownerId) {
      queryParams.append('ownerId', ownerId);
    }
    if (scope) {
      queryParams.append('scope', scope);
    }

    const response = await api.get(
      `${this.baseUrl}/dashboards/${dashboardId}?${queryParams}`,
    );
    return response.data;
  }

  // Create/Update dashboard (via Pleri agent)
  async createDashboard(
    data: CreateDashboardRequest,
  ): Promise<CreateDashboardResponse> {
    const response = await api.post(`${this.baseUrl}/dashboards`, data);
    return response.data;
  }
}

// Export singleton instance
export const dashboardService = new DashboardService();

// React Query hooks
export const useDashboards = (params: ListDashboardsRequest) => {
  return useQuery({
    queryKey: ['dashboards', params],
    queryFn: () => dashboardService.listDashboards(params),
  });
};

export const useDashboard = (
  dashboardId: string,
  _tenantId: string,
  _ownerId: string,
  _scope?: DashboardScope,
) => {
  return useQuery({
    queryKey: ['dashboard', dashboardId],
    queryFn: () => dashboardService.getDashboard(dashboardId),
    enabled: !!dashboardId,
  });
};
